import React, { useEffect, useMemo, useState } from "react";
import '../css/slogan.css';
/* how to use 

<AnimatedText
  text="Pokazywanie <s>słowo po słowie</s>"
  effect="showing"
  speed={250}
  startOnView={true}
/>

<AnimatedText text="Obviously you could try, but... nobody does it better than Negativ!" effect="typing" speed={40} startOnView={true} cursor={true} />

*/


type AnimatedTextProps = {
  text: string;
  effect?: "typing" | "showing";
  speed?: number;
  startOnView?: boolean;
  cursor?: boolean;
  className?: string;
  startAfterPageLoad?: boolean;
};

type Token = {
  text: string;
  strong?: boolean;
};

function parseText(input: string): Token[] {
  const tokens: Token[] = [];

  const regex = /<s>(.*?)<\/s>|([^<]+)/g;

  let match;

  while ((match = regex.exec(input)) !== null) {
    if (match[1]) {
      const parts = match[1].split(/(\s+)/);

      parts.forEach((part) => {
        if (part.length > 0) {
          tokens.push({
            text: part,
            strong: true,
          });
        }
      });
    }

    if (match[2]) {
      const parts = match[2].split(/(\s+)/);

      parts.forEach((part) => {
        if (part.length > 0) {
          tokens.push({
            text: part,
            strong: false,
          });
        }
      });
    }
  }

  return tokens;
}

export default function AnimatedText({
  text,
  effect = "typing",
  speed = 50,
  startOnView = true,
  cursor = true,
  className = "animated-slogan",
  startAfterPageLoad = false
}: AnimatedTextProps) {
  const tokens = useMemo(() => parseText(text), [text]);

  const [pageLoaded, setPageLoaded] = useState(!startAfterPageLoad);
  const [visibleCount, setVisibleCount] = useState(0);
  const [started, setStarted] = useState(!startOnView);

  const fullText = useMemo(() => {
    return tokens.map((t) => t.text).join("");
  }, [tokens]);

  // =========================================
  // PAGE LOAD
  // =========================================

  useEffect(() => {
    if (!startAfterPageLoad) return

    // jeśli strona już gotowa
    if (document.readyState === "complete") {
      setPageLoaded(true)
      return
    }

    const handleLoad = () => {
      setPageLoaded(true)
    }

    window.addEventListener("load", handleLoad)

    return () => {
      window.removeEventListener("load", handleLoad)
    }
  }, [startAfterPageLoad])

  // Intersection Observer
  const handleRef = (element: HTMLDivElement | null) => {
    if (!startOnView || !element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.2,
      }
    );

    observer.observe(element);
  };

  // Animacja
  useEffect(() => {
    if (!started && !pageLoaded) return;

    setVisibleCount(0);

    const interval = setInterval(() => {
      setVisibleCount((prev) => {
        if (effect === "typing") {
          if (prev >= fullText.length) {
            clearInterval(interval);
            return prev;
          }

          return prev + 1;
        }

        if (prev >= tokens.length) {
          clearInterval(interval);
          return prev;
        }

        return prev + 1;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [started, effect, speed, fullText.length, tokens.length, pageLoaded]);

  // ===== TYPING =====
  if (effect === "typing") {
    let currentLength = 0;

    return (
      <div ref={handleRef} className={className}>
        {tokens.map((token, index) => {
          const start = currentLength;
          const end = currentLength + token.text.length;

          currentLength = end;

          const visiblePart = token.text.slice(
            0,
            Math.max(0, visibleCount - start)
          );

          if (!visiblePart) return null;

          return (
            <span
              key={index}
              className={token.strong ? "animated-strong" : ""}
            >
              {visiblePart}
            </span>
          );
        })}

        {cursor && (
          <span className="animated-cursor">|</span>
        )}
      </div>
    );
  }

  // ===== SHOWING =====
  return (
    <div ref={handleRef} className={className}>
      {tokens.slice(0, visibleCount).map((token, index) => (
        <span
          key={index}
          className={token.strong ? "animated-strong" : ""}
        >
          {token.text}
        </span>
      ))}
    </div>
  );
}